

import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../../services/auth/auth.service';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { phoneNumberValidator } from 'src/app/shared/validators/phone-number.validator';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';
import { handleFileUploaded, handleFileUploadError } from 'src/app/shared/components/upload-input/file-upload-helper';


@Component({
  selector: 'app-admin-details-modal',
  standalone: false,
  templateUrl: './admin-details-modal.component.html',
  styleUrl: './admin-details-modal.component.scss'
})
export class AdminDetailsModalComponent {
  form: FormGroup;
  isDarkMode!: boolean;

  details: any;


  isLoadingPrivileges: boolean = true;

  isSubmitting: boolean = false;
  isSubmitAttempted: boolean = false;
  isLoading: boolean;

  showErrorMessages: boolean = false;

  ticketList: any[] = [];
  vehicleTypes: any[] = [];

  rememberMe: boolean;


  isInEditingMode: boolean = false;

  roles: any[];

  lgaList: any[]
  zoneList: any[];

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AdminDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private apiService: ApiService,
    private utilitiesService: UtilitiesService
  ) { }



  ngOnInit(): void {
    this.initForm();

    this.getAllVehicleTypes();

    this.form.get('vehicleTypeId')?.valueChanges.subscribe((id) => {
      const vehicle = this.vehicleTypes.find((v) => v.id === id);
      this.form.patchValue({ vehicleTypeName: vehicle ? vehicle.name : '' });
    });

    this.rememberMe = false;



    this.isInEditingMode = Boolean(this.dialogData?.isInEditingMode);

    if (!this.isInEditingMode) {
      this.form.disable()
    }



    if (this.dialogData?.id) {
      this.fetchDetails()
    }

    this.getRoles();
    this.loadLgaList();
  }

  onFileChange(
      fileDetails: { file: File; readableUrl: string } | null,
      controlName: string
    ) {
      handleFileUploaded(fileDetails, this.form, controlName);
    }
  
    onError(errorResponse: any, controlName: string) {
      handleFileUploadError(errorResponse, this.form, controlName);
    }

  loadLgaList() {
    this.utilitiesService.getLocalGovernmentList().subscribe(
      (response) => {
        this.lgaList = response;
      },
      (error) => {
        console.error('Error Fetching Local Government');
      }
    );
  }

  getRoles() {
    this.apiService
      .get('/roles/all', {})
      .subscribe(
        (response) => {

          this.roles = response.data;

        },
        (error) => {

          this.openErrorModal(error?.message)

        }
      );
  }

  initForm(): void {
    this.form = this.fb.group({
      phoneNumber: ['', [Validators.required, phoneNumberValidator]],
      address: ['', [Validators.required]],
      role: [null, [Validators.required]],
      lga: [null, [Validators.required]],
      zone: [null, [Validators.required]],
      displayName: [null, [Validators.required]],
      email: [null, [Validators.required]],
    });


    this.subscribeToLgaChanges()


  }


  get lgaControl() {
    return this.form.get('lga') as FormControl
  }

  get zoneControl() {
    return this.form.get('zone') as FormControl
  }

  allowEditing() {
    this.isInEditingMode = true;
    this.form.enable()
  }

  fetchDetails() {
    this.details = this.dialogData
    this.mapFormValues(this.dialogData)

    this.apiService
      .get(`/auth/admin/details/${this.dialogData?.id}`)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        if (response.success) {
          this.details = response?.data
          this.mapFormValues(response?.data)
        }
      });
  }



  subscribeToLgaChanges() {
    this.lgaControl.valueChanges.subscribe(lga => {
      this.zoneList = null;
      this.zoneControl.reset()
      this.getZonesFromLga(lga);
    })
  }

  getZonesFromLga(lga) {
    if (lga) {
      this.utilitiesService.getZonesFromLga(lga).subscribe((res: any) => {
        this.zoneList = res;
      });
    }
    else {
      console.error('error loading zones')
    }
  }

  mapFormValues(data: any) {
    this.form.patchValue({
      phoneNumber: data?.phoneNumber,
      address: data?.address,
      role: data.roleId,
      lga: data.lgaId,
      zone: data.zoneId,
      email: data.email,
      displayName: data.displayName,
    })
  }

  getAllVehicleTypes() {
    this.isLoading = true;
    this.apiService
      .get('/vehicle-type/all')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.vehicleTypes = response.data.map((vehicleType) => ({
          ...vehicleType,
          formattedName: this.toTitleCase(vehicleType.name.replace(/_/g, ' ')),
        }));
      });
  }

  toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }


  closeModal(): void {
    if (!this.isSubmitting) {
      this.dialogRef.close();
    }
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      location.reload();
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  openConfirmModal() {
    let dialogRef = this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      disableClose: true,

      data: {
        title: 'Are you sure you want to update this user?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.updateUser();
        },
        onCancel: () => {
          // dialogRef.close()
        },
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  addToTicketList(): void {
    if (this.form.valid) {
      this.ticketList.push(this.form.value);
      this.form.reset();
      this.showErrorMessages = false

    } else {
      this.showErrorMessages = true
    }
  }

  removeTicket(index: number): void {
    this.ticketList.splice(index, 1);
  }

  convertToBase64(file: File | null): Promise<string> {
    return new Promise((resolve, reject) => {
      if (file && file instanceof File) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result as string;
          resolve(base64String);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file); // Proceed with the conversion if file is valid
      } else {
        reject(new Error("The provided input is not a valid file."));
      }
    });
  }


  async updateUser() {
    this.isSubmitting = true;
    // this.form.disable();

    const formValues = this.form?.getRawValue();
    const photoBase64 = formValues.photo?.file
          ? await this.convertToBase64(formValues.photo?.file)
          : null;

    const request = {
      roleId: formValues.role,
      lgaId: formValues.lga,
      zoneId: formValues.zone,
      displayName: formValues.displayName,
      address: formValues.address,
      phoneNumber: formValues.phoneNumber,
      email: formValues.email,
    };

    this.apiService
      .post(`/auth/update-admin/${this.dialogData?.id}`, request)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.form.enable()
        })
      )
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.openSuccessModal('User updated successfuly');
          }

          this.dialogRef.close();
        },
        (error) => {
          this.openErrorModal(error?.message);
        }
      );

    this.isSubmitAttempted = true;
  }
}
